import * as pc from "playcanvas";

export type CharState =
  | "idle" | "idle_knife" | "run" | "walk" | "run_back" | "walk_back"
  | "strafe_l" | "strafe_r"
  | "crouch_idle" | "crouch_fwd" | "crouch_back" | "crouch_left" | "crouch_right"
  | "reload_idle" | "reload_walk" | "reload_run" | "reload_crouch"
  | "grenade" | "jump" | "death" | "dance" | "defeated"
  | "stretch" | "rifle_walk_stop";

/** Halftime side-swap mannequin poses (random pick per player). */
export type HalftimePose = "stretch" | "rifle_walk_stop";

/** Below this horizontal speed (m/s) the character is considered idle. */
const MOVE_EPS = 0.6;
const BLEND_LOCO = 0.15;
const BLEND_ACTION = 0.1;
/** How long the grenade-toss clip owns the body before locomotion resumes. */
const THROW_LOCK_SEC = 1.5;
/** Max time the jump clip owns the body. The local shadow ends it exactly
 *  on landing via notifyGrounded(); this is the fallback for peers (whose
 *  landing we can't observe) and a safety cap. ~ the 0.7 s air time. */
const JUMP_MAX_LOCK_SEC = 0.85;
/** Keep the jump up at least this long, so the local shadow doesn't cancel
 *  it during the few frames between the Space press and leaving the ground. */
const JUMP_MIN_SEC = 0.12;

/**
 * RemoteCharacter — wraps a skinned Mixamo character entity + its anim
 * component and exposes high-level intent (move / reload / die) that it
 * maps to crossfaded animation states. The entity is parented by the
 * owning RemotePlayer (under its root), so position/yaw come for free;
 * this class only drives the animation state machine.
 */
export class RemoteCharacter {
  readonly entity: pc.Entity;
  private readonly anim: pc.AnimComponent;
  /** States that actually got a clip assigned (guards against failed loads). */
  private readonly available: ReadonlySet<string>;
  /** Cached right-hand (wrist) bone — the weapon attaches/tracks here. */
  private rightHand: pc.Entity | null | undefined;
  /** Cached hips (skeleton root) bone — carries any clip root motion. */
  private hips: pc.Entity | null | undefined;
  private state: CharState = "idle";
  private dead = false;
  /** Terminal match-result pose (dance / defeated) — locks locomotion out
   *  like death does, so applyMotion can't blend back to idle. */
  private resultLocked = false;
  private reloading = false;
  /** performance.now() until which the grenade toss owns the body. */
  private throwUntil = 0;
  /** performance.now() until which the jump clip owns the body. */
  private jumpUntil = 0;
  private jumpStart = 0;

  constructor(entity: pc.Entity, anim: pc.AnimComponent, available: ReadonlySet<string>) {
    this.entity = entity;
    this.anim = anim;
    this.available = available;
  }

  /**
   * Drive the pose from world-space velocity + facing yaw + state flags.
   * Priority: reload (stance-matched variant) > crouch (idle/walk 4-dir) >
   * standing idle (relaxed for the knife) / run|walk / strafe / backpedal.
   * Ignored while a one-shot action (throw / jump / death) owns the body.
   */
  applyMotion(
    vx: number, vz: number, yawDeg: number,
    walking: boolean, knife: boolean, crouch: boolean,
  ): void {
    const now = performance.now();
    if (this.dead || this.resultLocked || now < this.throwUntil || now < this.jumpUntil) return;

    const speed = Math.hypot(vx, vz);
    const moving = speed >= MOVE_EPS;
    // Game convention: yaw 0 faces -Z. forward=(-sin,-cos), right=(cos,-sin).
    const yr = (yawDeg * Math.PI) / 180;
    const sin = Math.sin(yr);
    const cos = Math.cos(yr);
    const relFwd = -(vx * sin + vz * cos);
    const relRight = vx * cos - vz * sin;
    const fwdDominant = Math.abs(relFwd) >= Math.abs(relRight);

    let next: CharState;
    if (this.reloading && this.available.has("reload_idle")) {
      // Stance-matched reload (falls back to reload_idle if a variant is missing).
      if (crouch && this.available.has("reload_crouch")) next = "reload_crouch";
      else if (moving && walking && this.available.has("reload_walk")) next = "reload_walk";
      else if (moving && this.available.has("reload_run")) next = "reload_run";
      else next = "reload_idle";
    } else if (crouch && this.available.has("crouch_idle")) {
      if (!moving) next = "crouch_idle";
      else if (fwdDominant) next = relFwd >= 0 ? "crouch_fwd" : "crouch_back";
      else next = relRight >= 0 ? "crouch_right" : "crouch_left";
    } else if (!moving) {
      next = knife && this.available.has("idle_knife") ? "idle_knife" : "idle";
    } else if (fwdDominant) {
      next = relFwd >= 0 ? (walking ? "walk" : "run") : (walking ? "walk_back" : "run_back");
    } else {
      next = relRight >= 0 ? "strafe_r" : "strafe_l";
    }
    this.transition(next, BLEND_LOCO);
  }

  /** Reload flag — applyMotion picks the stance-matched reload variant. */
  setReloading(reloading: boolean): void {
    this.reloading = reloading;
  }

  /**
   * The Mixamo right-hand (wrist) bone, looked up + cached. Drives the
   * held-weapon position so the gun tracks the animated hand. Returns null
   * if the skeleton doesn't expose it (the caller then falls back to a body
   * offset).
   */
  getRightHand(): pc.Entity | null {
    if (this.rightHand === undefined) {
      this.rightHand = this.entity.findByName("mixamorig:RightHand") as pc.Entity | null;
    }
    return this.rightHand;
  }

  /** The Mixamo hips bone — the visual body's true position when a clip
   *  carries root motion (the entity root doesn't move). The halftime
   *  cinematic camera tracks it so the mannequin stays centred. */
  getHips(): pc.Entity | null {
    if (this.hips === undefined) {
      this.hips = this.entity.findByName("mixamorig:Hips") as pc.Entity | null;
    }
    return this.hips;
  }

  /** Play the grenade-toss one-shot; locomotion resumes after the lock. */
  playThrow(): void {
    if (this.dead) return;
    this.throwUntil = performance.now() + THROW_LOCK_SEC * 1000;
    this.transition("grenade", BLEND_ACTION);
  }

  /** Play the jump one-shot (edge-triggered); ignored if already jumping. */
  playJump(): void {
    if (this.dead) return;
    const now = performance.now();
    if (now < this.jumpUntil) return;
    this.jumpStart = now;
    this.jumpUntil = now + JUMP_MAX_LOCK_SEC * 1000;
    this.transition("jump", BLEND_ACTION);
  }

  /** Local player is on the ground — end the jump now so the pose matches
   *  the real air time, after a short minimum to clear the takeoff gap. */
  notifyGrounded(): void {
    if (this.jumpUntil === 0) return;
    if (performance.now() - this.jumpStart >= JUMP_MIN_SEC * 1000) this.jumpUntil = 0;
  }

  die(): void {
    if (this.dead) return;
    this.dead = true;
    this.transition("death", BLEND_ACTION);
  }

  /** Match-result pose for the end screen: silly dance on a win, defeat
   *  slump on a loss. Terminal until revive(). */
  playResult(won: boolean): void {
    this.resultLocked = true;
    this.transition(won ? "dance" : "defeated", BLEND_LOCO);
  }

  /** Halftime side-swap pose — locks locomotion out like playResult so the
   *  mannequin holds the cinematic clip. Terminal until revive(). */
  playHalftime(pose: HalftimePose): void {
    this.resultLocked = true;
    this.transition(pose, BLEND_LOCO);
  }

  revive(): void {
    this.dead = false;
    this.resultLocked = false;
    this.reloading = false;
    this.throwUntil = 0;
    this.jumpUntil = 0;
    this.transition("idle", BLEND_ACTION);
  }

  destroy(): void {
    this.entity.destroy();
  }

  private transition(to: CharState, time: number): void {
    if (to === this.state) return;
    if (!this.available.has(to)) return; // clip failed to load — keep current pose
    this.state = to;
    // baseLayer is non-null once a state graph exists (set up in the factory).
    this.anim.baseLayer?.transition(to, time);
  }
}
